import type { ProjectState, StateDocument } from '@angelmyth/contracts';

export type SessionUser = { id: string; username: string; displayName: string; role: string };
export type LaunchPlanDocument = {
  id: string;
  revision: number;
  data: Record<string, unknown>;
  createdBy: string;
  creatorName?: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  autoMerged?: boolean;
  resolvedConflicts?: string[];
};

export type StylePoolImage = {
  id: string;
  assetId: string;
  url: string;
  originalName: string;
  width: number | null;
  height: number | null;
  createdAt: string;
  source?: 'manual' | 'plan';
  planIds?: string[];
  planNames?: string[];
};

export type StylePoolPage = {
  images: StylePoolImage[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
};

export type AjidouPoolImage = StylePoolImage & { planIds: string[]; planNames: string[] };
export type AjidouPoolPage = Omit<StylePoolPage, 'images'> & { images: AjidouPoolImage[] };

const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiClientError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly details?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body != null && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const requestInit: RequestInit = { ...init, credentials: 'include', headers };
  const response = await fetch(`${apiBase}${path}`, requestInit);
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { code?: string; message?: string; details?: unknown } } | null;
    throw new ApiClientError(response.status, payload?.error?.code ?? 'REQUEST_FAILED', payload?.error?.message ?? `请求失败 (${response.status})`, payload?.error?.details);
  }
  return response.json() as Promise<T>;
}

async function uploadAsset(file: File, projectSlug: string, options: { preserveOriginal?: boolean } = {}) {
  const form = new FormData();
  form.append('projectSlug', projectSlug);
  form.append('uploadPolicy', options.preserveOriginal ? 'preserve-original' : 'reference-compressed');
  form.append('file', file);
  return request<{ id: string; url: string; mimeType: string; size: number; width: number | null; height: number | null; createdAt: string }>('/api/assets', { method: 'POST', body: form });
}

export const api = {
  getSession: () => request<{ authenticated: boolean; user: SessionUser | null }>('/api/auth/session'),
  login: (username: string, password: string) => request<{ ok: boolean; user: SessionUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST', body: JSON.stringify({}) }),
  changePassword: (currentPassword: string, newPassword: string) => request<{ ok: boolean }>('/api/auth/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),
  getState: (projectSlug: string) => request<StateDocument>(`/api/projects/${projectSlug}`),
  updateState: (projectSlug: string, expectedRevision: number, data: ProjectState) => request<StateDocument>(`/api/projects/${projectSlug}`, { method: 'PUT', body: JSON.stringify({ expectedRevision, data }) }),
  updateProfitPrices: (projectSlug: string, prices: number[]) => request<StateDocument>(`/api/projects/${projectSlug}/profit-prices`, { method: 'PUT', body: JSON.stringify({ prices }) }),
  getLaunchPlans: (projectSlug: string) => request<{ plans: LaunchPlanDocument[] }>(`/api/projects/${projectSlug}/launch-plans`),
  createLaunchPlan: (projectSlug: string, data: Record<string, unknown>) => request<LaunchPlanDocument>(`/api/projects/${projectSlug}/launch-plans`, { method: 'POST', body: JSON.stringify({ data }) }),
  updateLaunchPlan: (projectSlug: string, planId: string, expectedRevision: number, baseData: Record<string, unknown>, data: Record<string, unknown>) => request<LaunchPlanDocument>(`/api/projects/${projectSlug}/launch-plans/${encodeURIComponent(planId)}`, { method: 'PUT', body: JSON.stringify({ expectedRevision, baseData, data }) }),
  getLaunchPlanPresence: (projectSlug: string, planId: string, path = '*') => request<{ editors: Array<{ userId: string; displayName: string; path: string; lastSeen: string }> }>(`/api/projects/${projectSlug}/launch-plans/${encodeURIComponent(planId)}/presence?path=${encodeURIComponent(path)}`),
  heartbeatLaunchPlan: (projectSlug: string, planId: string, path = '*') => request<{ ok: true }>(`/api/projects/${projectSlug}/launch-plans/${encodeURIComponent(planId)}/presence`, { method: 'POST', body: JSON.stringify({ path }) }),
  clearLaunchPlanPresence: (projectSlug: string, planId: string, path = '*') => request<{ ok: true }>(`/api/projects/${projectSlug}/launch-plans/${encodeURIComponent(planId)}/presence?path=${encodeURIComponent(path)}`, { method: 'DELETE' }),
  deleteLaunchPlan: (projectSlug: string, planId: string) => request<{ ok: boolean }>(`/api/projects/${projectSlug}/launch-plans/${encodeURIComponent(planId)}`, { method: 'DELETE' }),
  uploadImage: async (file: File, projectSlug: string, options: { preserveOriginal?: boolean } = {}): Promise<string> => `${apiBase}${(await uploadAsset(file, projectSlug, options)).url}`,
  listStylePoolImages: (projectSlug: string, styleKey: string, page = 1, limit = 100) => request<StylePoolPage>(`/api/projects/${projectSlug}/style-pool/${encodeURIComponent(styleKey)}?page=${page}&limit=${limit}`),
  addStylePoolImage: async (projectSlug: string, styleKey: string, file: File): Promise<StylePoolImage> => {
    const asset = await uploadAsset(file, projectSlug);
    const image = await request<StylePoolImage>(`/api/projects/${projectSlug}/style-pool/${encodeURIComponent(styleKey)}`, { method: 'POST', body: JSON.stringify({ assetId: asset.id }) });
    return { ...image, url: `${apiBase}${image.url}` };
  },
  deleteStylePoolImage: (projectSlug: string, imageId: string) => request<{ ok: true }>(`/api/projects/${projectSlug}/style-pool/image/${encodeURIComponent(imageId)}`, { method: 'DELETE' }),
  deleteAssetByUrl: (url: string) => request<{ ok: boolean; deleted: boolean }>(`/api/assets/by-url?url=${encodeURIComponent(url)}`, { method: 'DELETE' }),
  listAjidouPoolImages: (projectSlug: string, page = 1, limit = 100) => request<AjidouPoolPage>(`/api/projects/${projectSlug}/ajidou-pool?page=${page}&limit=${limit}`),
};
